/**
 * Generate code snippets (cURL, JS fetch, Python requests, PHP cURL) from the
 * active try-it state. Snippets reflect the user's current params/body live
 * so they can copy-paste straight into their codebase.
 *
 * Auth is shown as a placeholder `<YOUR_JWT>` rather than the actual session
 * value — copying real tokens into snippets is a security footgun.
 */

import type { EndpointInfo } from '@/api/client';
import type { TryItState } from '@/store/tryIt';

export type SnippetLanguage = 'curl' | 'js' | 'python' | 'php';

const PROD_BASE = 'https://pub.balesotomatis.co.id';

export function generateSnippet(
  lang: SnippetLanguage,
  endpoint: EndpointInfo,
  state: TryItState
): string {
  const url = buildUrlForSnippet(endpoint.path, state.pathParams, state.queryParams);
  const headers = buildHeaders(endpoint, state);
  const body = buildBodyText(endpoint, state);

  switch (lang) {
    case 'curl':
      return curlSnippet(endpoint.methodUpper, url, headers, body);
    case 'js':
      return jsSnippet(endpoint.methodUpper, url, headers, body);
    case 'python':
      return pythonSnippet(endpoint.methodUpper, url, headers, body);
    case 'php':
      return phpSnippet(endpoint.methodUpper, url, headers, body);
  }
}

function buildUrlForSnippet(
  path: string,
  pathParams: Record<string, string>,
  queryParams: Record<string, string>
): string {
  let resolved = path;
  for (const [k, v] of Object.entries(pathParams)) {
    resolved = resolved.replace(`{${k}}`, v ? encodeURIComponent(v) : `{${k}}`);
  }
  const qs = Object.entries(queryParams)
    .filter(([, v]) => v !== '' && v != null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return `${PROD_BASE}${resolved}${qs ? `?${qs}` : ''}`;
}

function buildHeaders(
  endpoint: EndpointInfo,
  state: TryItState
): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: 'Bearer <YOUR_JWT>',
    Accept: 'application/json',
  };
  const hasBody = ['POST', 'PUT', 'PATCH'].includes(endpoint.methodUpper);
  if (hasBody) headers['Content-Type'] = 'application/json';
  // User overrides
  for (const [k, v] of Object.entries(state.headerParams)) {
    if (v) headers[k] = v;
  }
  return headers;
}

function buildBodyText(endpoint: EndpointInfo, state: TryItState): string | null {
  const hasBody = ['POST', 'PUT', 'PATCH'].includes(endpoint.methodUpper);
  if (!hasBody) return null;
  if (state.body && state.body.trim()) {
    try {
      return JSON.stringify(JSON.parse(state.body), null, 2);
    } catch {
      return state.body;
    }
  }
  return '{}';
}

// ── cURL ──────────────────────────────────────────────────────────────────

function curlSnippet(
  method: string,
  url: string,
  headers: Record<string, string>,
  body: string | null
): string {
  const lines = [`curl --request ${method} \\`, `  --url '${url}'`];
  for (const [k, v] of Object.entries(headers)) {
    lines[lines.length - 1] += ' \\';
    lines.push(`  --header '${k}: ${v}'`);
  }
  if (body) {
    lines[lines.length - 1] += ' \\';
    lines.push(`  --data '${body.replace(/\n/g, '')}'`);
  }
  return lines.join('\n');
}

// ── JS fetch ──────────────────────────────────────────────────────────────

function jsSnippet(
  method: string,
  url: string,
  headers: Record<string, string>,
  body: string | null
): string {
  const opts: string[] = [`method: '${method}'`];
  const hdrStr = Object.entries(headers)
    .map(([k, v]) => `    '${k}': '${v}'`)
    .join(',\n');
  opts.push(`headers: {\n${hdrStr}\n  }`);
  if (body) opts.push(`body: ${JSON.stringify(body)}`);
  return [
    `const response = await fetch('${url}', {`,
    ...opts.map((o, i) => '  ' + o + (i < opts.length - 1 ? ',' : '')),
    `});`,
    ``,
    `const data = await response.json();`,
    `console.log(data);`,
  ].join('\n');
}

// ── Python requests ───────────────────────────────────────────────────────

function pythonSnippet(
  method: string,
  url: string,
  headers: Record<string, string>,
  body: string | null
): string {
  const hdrLines = Object.entries(headers)
    .map(([k, v]) => `    '${k}': '${v}'`)
    .join(',\n');
  const lines = [
    `import requests`,
    ``,
    `url = '${url}'`,
    `headers = {`,
    hdrLines,
    `}`,
  ];
  if (body) {
    lines.push(``, `payload = ${body}`);
    lines.push(``, `response = requests.${method.toLowerCase()}(url, headers=headers, json=payload)`);
  } else {
    lines.push(``, `response = requests.${method.toLowerCase()}(url, headers=headers)`);
  }
  lines.push(``, `print(response.json())`);
  return lines.join('\n');
}

// ── PHP cURL ──────────────────────────────────────────────────────────────

function phpSnippet(
  method: string,
  url: string,
  headers: Record<string, string>,
  body: string | null
): string {
  const hdrLines = Object.entries(headers)
    .map(([k, v]) => `        '${k}: ${v}'`)
    .join(",\n");
  const lines = [
    `<?php`,
    `$ch = curl_init();`,
    `curl_setopt($ch, CURLOPT_URL, '${url}');`,
    `curl_setopt($ch, CURLOPT_CUSTOMREQUEST, '${method}');`,
    `curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);`,
    `curl_setopt($ch, CURLOPT_HTTPHEADER, [`,
    hdrLines,
    `]);`,
  ];
  if (body) {
    lines.push(`curl_setopt($ch, CURLOPT_POSTFIELDS, '${body.replace(/'/g, "\\'")}');`);
  }
  lines.push(``, `$response = curl_exec($ch);`, `curl_close($ch);`, `echo $response;`);
  return lines.join('\n');
}
